import { useState } from "react";
import BulkUploadButton from "../../components/BulkUploadButton";
import DataExportButton from "../../components/DataExportButton";
import {
  Alert,
  Box,
  Button,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { CategoryOutlined, CheckCircleOutline, PauseCircleOutline, UpdateOutlined } from "@mui/icons-material";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";

import {
  AppDeleteDialog,
  AppSearchField,
  AppToolbar,
} from "../../../../components/common";
import CategoryFormDialog from "../components/CategoryFormDialog";
import CategoryTable from "../components/CategoryTable";
import { useCategories } from "../hooks/useCategories";
import { Category, CategoryPayload } from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";

const CategoriesPage = () => {
  const {
    categories,
    loading,
    error,
    pagination,
    filters,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    updateCategoryStatus,
    searchCategories,
    changePage,
  } = useCategories();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );

  const handleCreate = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleSubmit = async (payload: CategoryPayload) => {
    try {
      setSaving(true);

      if (selectedCategory) {
        await updateCategory(selectedCategory.id, payload);
      } else {
        await createCategory(payload);
      }

      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteCategory(categoryToDelete.id);
      setDeleteOpen(false);
      setCategoryToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    searchCategories(value);
  };

  return (
    <Box
      sx={sparePageSx("categories")}
    >
      <Stack spacing={3}>
      <SparePageHeader kind="categories"
          title="Spare Categories"
          subtitle="Manage spare part category master data."
          action={
            <Stack direction="row" flexWrap="wrap" gap={1}>
            <BulkUploadButton kind="categories" onImported={fetchCategories} />
            <DataExportButton kind="categories" filters={filters} disabled={loading} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Category
            </Button>
            </Stack>
          }
        />

        {error && (
          <Alert severity="error" onClose={() => fetchCategories()}>
            {error}
          </Alert>
        )}

        <AppToolbar
          right={
            <SpareSummary items={[
              { label: "Total Categories", value: loading ? "—" : pagination?.total ?? 0, icon: <CategoryOutlined /> },
              { label: "Active · this page", value: loading ? "—" : categories.filter((item) => item.status === "ACTIVE").length, icon: <CheckCircleOutline />, color: "#10b981" },
              { label: "Inactive · this page", value: loading ? "—" : categories.filter((item) => item.status === "INACTIVE").length, icon: <PauseCircleOutline />, color: "#64748b" },
              { label: "Last updated · this page", value: loading ? "—" : categories.length ? new Date(Math.max(...categories.map((item) => new Date(item.updatedAt).getTime()).filter(Number.isFinite))).toLocaleDateString() : "—", icon: <UpdateOutlined />, color: "#8b5cf6" },
            ]} />
          }
          left={
            <AppSearchField
              value={search}
              fullWidth
              placeholder="Search category name, code, or reference..."
              onChange={handleSearchChange}
            />
          }
        />

        <CategoryTable
          rows={filterRowsBySearch(categories, search)}
          loading={loading}
          rowCount={pagination?.total || 0}
          page={(filters.page || 1) - 1}
          pageSize={filters.limit || 10}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={(category, status) =>
            updateCategoryStatus(category.id, status)
          }
          onPageChange={(page) =>
            changePage(page + 1, filters.limit || 10)
          }
          onPageSizeChange={(pageSize) =>
            changePage(1, pageSize)
          }
        />
      </Stack>

      <CategoryFormDialog
        open={dialogOpen}
        loading={saving}
        initialData={selectedCategory}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      <AppDeleteDialog
        open={deleteOpen}
        loading={deleteLoading}
        title="Delete Category"
        content={`Are you sure you want to delete ${
          categoryToDelete?.name || "this category"
        }? This will mark it inactive.`}
        onClose={() => {
          setDeleteOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default CategoriesPage;
